import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ChevronLeftIcon, ChevronRightIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
  getAssignmentFeinplanung,
  getAssignmentLinks,
  type PlanungAssignmentLinkPeer,
  type PlanungDayOrders,
  type PlanungFeinplanungMember,
} from "@/lib/montageplanung";
import { MONTH_NAMES, WEEKDAY_SHORT, formatDateDe, parseIso } from "./dateUtils";
import { splitOrderLinkLabel } from "./displayLabels";

function dayIso(workDate: string): string {
  return workDate.slice(0, 10);
}

function dayHasContent(day: PlanungDayOrders): boolean {
  return day.orders.length > 0 || !!day.note?.trim();
}

function daysInRange(days: PlanungDayOrders[], from: string, to: string): PlanungDayOrders[] {
  const start = from.slice(0, 10);
  const end = to.slice(0, 10);
  return days.filter((day) => {
    const iso = dayIso(day.work_date);
    return iso >= start && iso <= end;
  });
}

function memberSort(a: PlanungFeinplanungMember, b: PlanungFeinplanungMember): number {
  const byDate = a.date_from.slice(0, 10).localeCompare(b.date_from.slice(0, 10));
  if (byDate !== 0) return byDate;
  return a.id - b.id;
}

interface EmptyGap {
  kind: "gap";
  from: string;
  to: string;
  count: number;
}

interface PlannedDay {
  kind: "day";
  day: PlanungDayOrders;
}

type PreviewRow = EmptyGap | PlannedDay;

function collapseEmptyDays(days: PlanungDayOrders[]): PreviewRow[] {
  const rows: PreviewRow[] = [];
  let gapFrom = "";
  let gapTo = "";
  let gapCount = 0;

  function flushGap() {
    if (gapCount === 0) return;
    rows.push({ kind: "gap", from: gapFrom, to: gapTo, count: gapCount });
    gapFrom = "";
    gapTo = "";
    gapCount = 0;
  }

  for (const day of days) {
    if (!dayHasContent(day)) {
      const iso = dayIso(day.work_date);
      if (gapCount === 0) gapFrom = iso;
      gapTo = iso;
      gapCount += 1;
      continue;
    }
    flushGap();
    rows.push({ kind: "day", day });
  }
  flushGap();
  return rows;
}

function DayMark({ iso }: { iso: string }) {
  const date = parseIso(iso);
  return (
    <div className="w-11 shrink-0 pt-0.5 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
        {WEEKDAY_SHORT[date.getDay()]}
      </div>
      <div className="mt-0.5 text-xl font-semibold leading-none tabular-nums text-label">
        {date.getDate()}
      </div>
    </div>
  );
}

export interface FinePlanningPreviewProps {
  assignmentId: number;
  active: boolean;
  refreshKey?: number;
  /** Formular-Zeitraum weicht vom gespeicherten Einsatz ab. */
  datesStale?: boolean;
  editorCount?: number;
  /** Gespeicherter Zeitraum des geöffneten Einsatzes. */
  focusDateFrom?: string;
  focusDateTo?: string;
  focusTitle?: string;
  onEdit: () => void;
  onRelease: () => void;
}

export function FinePlanningPreview({
  assignmentId,
  active,
  refreshKey = 0,
  datesStale = false,
  editorCount = 0,
  focusDateFrom,
  focusDateTo,
  focusTitle = "",
  onEdit,
  onRelease,
}: FinePlanningPreviewProps) {
  const [days, setDays] = useState<PlanungDayOrders[]>([]);
  const [seriesMembers, setSeriesMembers] = useState<PlanungFeinplanungMember[]>([]);
  const [focusedMemberId, setFocusedMemberId] = useState(assignmentId);
  const [seriesMode, setSeriesMode] = useState(false);
  const [linkedCount, setLinkedCount] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFocusedMemberId(assignmentId);
  }, [assignmentId]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getAssignmentFeinplanung(assignmentId)
      .then(async (data) => {
        if (cancelled) return;
        setDays(data.days);
        setSeriesMode(!!data.series_mode);
        setLinkedCount(data.linked_assignment_ids?.length ?? 0);
        setCanEdit(!!data.can_edit);
        const fromApi = data.series_mode ? (data.series_members ?? []) : [];
        if (fromApi.length > 1) {
          setSeriesMembers(fromApi);
          return;
        }
        if (
          !data.series_mode ||
          !focusDateFrom ||
          !focusDateTo ||
          focusDateTo.slice(0, 10) < focusDateFrom.slice(0, 10)
        ) {
          setSeriesMembers([]);
          return;
        }
        let peers: PlanungAssignmentLinkPeer[] = [];
        try {
          peers = await getAssignmentLinks(assignmentId);
        } catch {
          peers = [];
        }
        if (cancelled) return;
        const members: PlanungFeinplanungMember[] = [
          {
            id: assignmentId,
            date_from: focusDateFrom,
            date_to: focusDateTo,
            title: focusTitle,
            days: daysInRange(data.days, focusDateFrom, focusDateTo),
          },
          ...peers.map((peer) => ({
            id: peer.id,
            date_from: peer.date_from,
            date_to: peer.date_to,
            title: peer.title,
            days: daysInRange(data.days, peer.date_from, peer.date_to),
          })),
        ];
        setSeriesMembers(members.sort(memberSort));
      })
      .catch((err) => {
        if (cancelled) return;
        setDays([]);
        setSeriesMembers([]);
        setError(err instanceof Error ? err.message : "Feinplanung konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, assignmentId, refreshKey, focusDateFrom, focusDateTo, focusTitle]);

  const focusedMember = useMemo(() => {
    if (seriesMembers.length < 2) return null;
    return (
      seriesMembers.find((member) => member.id === focusedMemberId) ??
      seriesMembers.find((member) => member.id === assignmentId) ??
      seriesMembers[0]
    );
  }, [seriesMembers, focusedMemberId, assignmentId]);

  const focusIndex = focusedMember
    ? seriesMembers.findIndex((member) => member.id === focusedMember.id)
    : -1;
  const previousMember = focusIndex > 0 ? seriesMembers[focusIndex - 1] : null;
  const nextMember =
    focusIndex >= 0 && focusIndex < seriesMembers.length - 1
      ? seriesMembers[focusIndex + 1]
      : null;

  const visibleDays = useMemo(() => {
    if (focusedMember) return focusedMember.days;
    if (seriesMode && focusDateFrom && focusDateTo) {
      return daysInRange(days, focusDateFrom, focusDateTo);
    }
    return days;
  }, [focusedMember, seriesMode, focusDateFrom, focusDateTo, days]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [focusedMember?.id]);

  const plannedCount = useMemo(() => visibleDays.filter(dayHasContent).length, [visibleDays]);
  const rows = useMemo(() => collapseEmptyDays(visibleDays), [visibleDays]);
  const monthBreaks = useMemo(() => {
    const marks = new Set<string>();
    let last = "";
    for (const row of rows) {
      const iso = row.kind === "day" ? dayIso(row.day.work_date) : row.from;
      const month = iso.slice(0, 7);
      if (month !== last) {
        marks.add(row.kind === "day" ? dayIso(row.day.work_date) : `gap:${row.from}`);
        last = month;
      }
    }
    return marks;
  }, [rows]);

  const rangeLabel =
    visibleDays.length > 0
      ? `${formatDateDe(dayIso(visibleDays[0]!.work_date))} – ${formatDateDe(dayIso(visibleDays[visibleDays.length - 1]!.work_date))}`
      : "";

  return (
    <section className="flex min-h-[280px] min-w-0 flex-col lg:min-h-0 lg:overflow-hidden">
      <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-label">Feinplanung</h2>
            {seriesMode ? (
              <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                Serie
              </span>
            ) : linkedCount > 0 ? (
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-label-secondary">
                Verknüpft
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-label-secondary">
            {loading
              ? "Wird geladen…"
              : visibleDays.length === 0
                ? "Noch kein Zeitraum geladen."
                : plannedCount === 0
                  ? `${visibleDays.length} Tag${visibleDays.length === 1 ? "" : "e"} ohne Eintrag`
                  : `${plannedCount} von ${visibleDays.length} Tagen geplant`}
            {rangeLabel && !focusedMember ? ` · ${rangeLabel}` : ""}
          </p>
          {editorCount > 0 && (
            <p className="mt-1 text-xs text-label-tertiary">
              Freigegeben für {editorCount} Mitarbeiter.
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" size="sm" onClick={onRelease}>
            Freigeben
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onEdit}
            disabled={datesStale || loading}
            title={datesStale ? "Bitte zuerst den geänderten Zeitraum speichern." : undefined}
            leftIcon={<PencilSquareIcon className="h-4 w-4" aria-hidden />}
          >
            {loading || canEdit ? "Bearbeiten" : "Ansehen"}
          </Button>
        </div>
      </div>

      {datesStale && (
        <Alert variant="info" className="mb-3 shrink-0">
          Der Zeitraum im Formular ist noch nicht gespeichert. Die Übersicht zeigt die gespeicherte
          Feinplanung.
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mb-3 shrink-0">
          {error}
        </Alert>
      )}

      {focusedMember && !loading && !error && (
        <div className="mb-3 flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-label-secondary ring-1 ring-inset ring-separator transition hover:bg-fill-tertiary hover:text-label disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            disabled={!previousMember}
            aria-label="Vorherigen Einsatz anzeigen"
            onClick={() => previousMember && setFocusedMemberId(previousMember.id)}
          >
            <ChevronLeftIcon className="h-4 w-4" aria-hidden />
          </button>
          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-xs font-medium text-label">
              {formatDateDe(focusedMember.date_from)} – {formatDateDe(focusedMember.date_to)}
            </p>
            <p className="text-[11px] text-label-tertiary">
              {focusedMember.id === assignmentId
                ? "Dieser Einsatz"
                : focusedMember.title.trim() || "Weiterer Einsatz"}
              {` · ${focusIndex + 1} von ${seriesMembers.length}`}
            </p>
            {focusedMember.id !== assignmentId && (
              <button
                type="button"
                className="mt-0.5 text-[11px] font-medium text-accent hover:underline"
                onClick={() => setFocusedMemberId(assignmentId)}
              >
                Zu diesem Einsatz
              </button>
            )}
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-label-secondary ring-1 ring-inset ring-separator transition hover:bg-fill-tertiary hover:text-label disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            disabled={!nextMember}
            aria-label="Nächsten Einsatz anzeigen"
            onClick={() => nextMember && setFocusedMemberId(nextMember.id)}
          >
            <ChevronRightIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      <div ref={listRef} className="min-h-0 flex-1 lg:overflow-y-auto lg:pr-1">
        {loading && (
          <div className="space-y-3" aria-hidden>
            {[0, 1, 2].map((item) => (
              <div key={item} className="flex gap-3">
                <div className="h-10 w-11 rounded-lg bg-fill-tertiary/80" />
                <div className="h-16 flex-1 rounded-2xl bg-fill-tertiary/70" />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && plannedCount === 0 && visibleDays.length > 0 && (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-separator bg-card/60 px-6 py-10 text-center">
            <p className="text-sm font-medium text-label">Noch nichts für die Tage eingeplant</p>
            <p className="mt-1 max-w-xs text-xs leading-relaxed text-label-secondary">
              {seriesMode
                ? "Für diesen Einsatz ist noch nichts eingeplant. Andere Termine der Serie können Sie darüber öffnen."
                : "Ordnen Sie Aufträge und Notizen den einzelnen Tagen zu."}
            </p>
          </div>
        )}

        {!loading && !error && plannedCount > 0 && (
          <ol className="space-y-4">
            {rows.map((row) => {
              const markKey = row.kind === "day" ? dayIso(row.day.work_date) : `gap:${row.from}`;
              const monthIso = row.kind === "day" ? dayIso(row.day.work_date) : row.from;
              const showMonth = monthBreaks.has(markKey);
              const monthDate = parseIso(monthIso);
              return (
                <li key={markKey}>
                  {showMonth && (
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-label-tertiary">
                      {MONTH_NAMES[monthDate.getMonth()]} {monthDate.getFullYear()}
                    </p>
                  )}
                  {row.kind === "gap" ? (
                    <p className="rounded-xl bg-card/50 px-3 py-2 text-xs text-label-tertiary">
                      {row.count === 1
                        ? `${formatDateDe(row.from)} ohne Eintrag`
                        : `${row.count} Tage ohne Eintrag · ${formatDateDe(row.from)} – ${formatDateDe(row.to)}`}
                    </p>
                  ) : (
                    <article className="flex gap-3">
                      <DayMark iso={dayIso(row.day.work_date)} />
                      <div
                        className={clsx(
                          "min-w-0 flex-1 rounded-2xl bg-card px-3.5 py-3 shadow-sm ring-1 ring-inset ring-separator",
                        )}
                      >
                        {row.day.orders.length > 0 && (
                          <ul className="space-y-2">
                            {row.day.orders.map((order) => {
                              const { number, details } = splitOrderLinkLabel(
                                order.order_id,
                                order.order_label,
                              );
                              return (
                                <li key={order.order_id} className="min-w-0">
                                  <div className="font-mono text-[13px] font-semibold leading-tight tracking-tight text-label">
                                    {number}
                                  </div>
                                  {details ? (
                                    <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-label-secondary">
                                      {details}
                                    </div>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {row.day.note?.trim() ? (
                          <p
                            className={clsx(
                              "whitespace-pre-wrap text-xs leading-relaxed text-label-secondary",
                              row.day.orders.length > 0 &&
                                "mt-2.5 border-t border-separator pt-2.5",
                            )}
                          >
                            {row.day.note.trim()}
                          </p>
                        ) : null}
                      </div>
                    </article>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
